/**
 * Reading `TranslatableText` safely.
 *
 * Contract §2: when `translated` is true the `text` field is machine output and
 * `original_text` holds the untouched original. Two rules follow, and both are
 * enforced here rather than at each call site:
 *
 *   - a translation is never presented as the original, and
 *   - anything the user is about to write back is composed from the original.
 */

import type { TextFormat, TranslatableText } from "../api/contract";

/** The one `TextFormat` member that changes how a value is rendered. */
const HTML: TextFormat = "HTML";

/**
 * True when this value is markup rather than prose.
 *
 * Ivanti's rich text editor writes HTML into `Incident.Symptom`, screenshots
 * included as base64 `data:` URIs, and the server says so on the field rather
 * than leaving the client to sniff for tags. It has already been sanitised by
 * the time it carries this flag — see `backend/src/it_helpdesk_agent/richtext.py`.
 *
 * There is no `showOriginal` variant of this on purpose: `format` describes
 * `text`, and a HTML value is never machine-translated (the server refuses to
 * send markup to a translator), so the flag holds for whichever string
 * {@link displayText} returns.
 */
export function isHtmlFormat(value: TranslatableText | null | undefined): boolean {
  return value?.format === HTML;
}

/** True when the server machine-translated this field. */
export function isMachineTranslated(value: TranslatableText | null | undefined): boolean {
  return value?.translated === true && typeof value.original_text === "string";
}

/**
 * The string to display.
 *
 * `showOriginal` flips a translated field back to the source text. A field that
 * was never translated ignores the flag.
 */
export function displayText(
  value: TranslatableText | null | undefined,
  showOriginal = false,
): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (showOriginal && isMachineTranslated(value)) {
    return value.original_text ?? value.text;
  }
  return value.text;
}

/** The language tag of whatever {@link displayText} would return. */
export function displayLang(
  value: TranslatableText | null | undefined,
  showOriginal = false,
): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (showOriginal && isMachineTranslated(value)) {
    return value.original_lang ?? value.lang;
  }
  return value.lang;
}

/**
 * The text to quote when composing a reply.
 *
 * Always the original — audit rule in contract §2. Never `displayText`.
 */
export function sourceText(value: TranslatableText | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  return value.original_text ?? value.text;
}

/** True when any field on the object was machine-translated. */
export function anyTranslated(values: readonly (TranslatableText | null | undefined)[]): boolean {
  return values.some(isMachineTranslated);
}
